import React, { useLayoutEffect, useState } from 'react';
import type { FormInstance } from 'antd';
import { Card, Form, InputNumber, Select, Space, Spin, Tabs, Tooltip, Typography } from 'antd';
import { asPreviewCss, applyContentAlignCascadeToForm } from '../utils/bookDefaultTemplates';
import {
  buildAlignPositionHostStyle,
  HEADER_ALIGNMENT_HOST,
  isFullWidthTextAlign,
  linePositionStyle,
  PREVIEW_TEXT_CONTAINMENT,
  wrapHeaderStyleLayers,
} from '../utils/bookStyleCss';
import { SectionLabel, StyleFieldGroup } from './common/IconFormLabel';
import { LineStyleEditTabs } from './karmakanda/LineStyleEditCollapse';
import './karmakanda/StyleModalTabs.css';

type Props = {
  form: FormInstance;
  previewLoading?: boolean;
};

const PREVIEW_STYLE_KEYS = [
  'defaultHeaderStyle',
  'defaultLineStyle',
  'defaultNestedLineStyle',
  'defaultDeepNestedLineStyle',
  'defaultLinkStyle',
] as const;

function isPreviewStylesReady(values: Record<string, unknown>): boolean {
  return PREVIEW_STYLE_KEYS.every(
    (key) => values[key] != null && typeof values[key] === 'object',
  );
}

function buildLinePreviewCss(
  lineStyle: Record<string, unknown> | undefined,
  defaultTextColor?: string,
): React.CSSProperties {
  const css = asPreviewCss(lineStyle || {});
  const widthPercent = Number(lineStyle?.widthPercent);
  const span = lineStyle?.span;
  const displayRaw = lineStyle?.display;
  const display =
    displayRaw === 'block' || displayRaw === 'inline' || displayRaw === 'inline-block'
      ? displayRaw
      : 'block';
  let width: React.CSSProperties['width'] = '100%';
  const textAlign = lineStyle?.align ?? lineStyle?.textAlign;
  const resolvedTextAlign =
    textAlign === 'left' ||
    textAlign === 'center' ||
    textAlign === 'right' ||
    textAlign === 'justify' ||
    textAlign === 'justify-center'
      ? textAlign
      : undefined;
  if (!isFullWidthTextAlign(resolvedTextAlign)) {
    width = 'fit-content';
  }
  if (widthPercent > 0) {
    width = `${widthPercent}%`;
  } else if (span === 12) {
    width = '50%';
  }
  return {
    ...css,
    color:
      css.color ??
      (typeof defaultTextColor === 'string' && defaultTextColor.trim() ? defaultTextColor.trim() : undefined),
    display,
    width,
    ...PREVIEW_TEXT_CONTAINMENT,
    whiteSpace: 'pre-wrap' as const,
    lineHeight:
      Number(lineStyle?.lineHeight) > 0 ? Number(lineStyle?.lineHeight) : css.lineHeight ?? 1,
    ...linePositionStyle(resolvedTextAlign ?? css.textAlign),
  };
}

const previewRowStyle = (active: boolean): React.CSSProperties =>
  active
    ? {
        border: '1px solid #91caff',
        borderRadius: 6,
        padding: '4px 6px',
        background: '#f0f7ff',
      }
    : { padding: '2px 0' };

export const BookDefaultStylesEditor: React.FC<Props> = ({ form, previewLoading = false }) => {
  const [activeStyleTab, setActiveStyleTab] = useState('page');
  const [previewReady, setPreviewReady] = useState(false);
  const formSnapshot = Form.useWatch([], form);
  const [previewTextByTab, setPreviewTextByTab] = useState<Record<string, string>>({
    headerAdvanced: 'विधान शीर्षक',
    lineAdvanced: 'अथातः कर्म प्रारभ्यते।\nसम्पूर्णं कर्मानुष्ठानं कर्तव्यम्।',
    nestedLineAdvanced: 'उपविधि — सम्पूर्णं कर्मानुष्ठानम्।',
    deepNestedLineAdvanced: 'तदनन्तरं ग्रहणं कर्तव्यम्।',
    linkAdvanced: 'अन्य ग्रन्थ',
  });

  const showListControls = activeStyleTab !== 'page';
  const activePreviewText = previewTextByTab[activeStyleTab] ?? '';
  const updateActivePreviewText = (next: string) =>
    setPreviewTextByTab((prev) => ({ ...prev, [activeStyleTab]: next }));

  const headerPreviewText = previewTextByTab.headerAdvanced ?? 'विधान शीर्षक';
  const linePreviewText =
    previewTextByTab.lineAdvanced ?? 'अथातः कर्म प्रारभ्यते।\nसम्पूर्णं कर्मानुष्ठानं कर्तव्यम्।';
  const nestedLinePreviewText = previewTextByTab.nestedLineAdvanced ?? 'उपविधि — सम्पूर्णं कर्मानुष्ठानम्।';
  const deepNestedLinePreviewText = previewTextByTab.deepNestedLineAdvanced ?? 'तदनन्तरं ग्रहणं कर्तव्यम्।';
  const linkPreviewText = previewTextByTab.linkAdvanced ?? 'अन्य ग्रन्थ';

  useLayoutEffect(() => {
    if (previewLoading) {
      setPreviewReady(false);
      return;
    }
    const values = (formSnapshot ?? form.getFieldsValue(true)) as Record<string, unknown>;
    if (!isPreviewStylesReady(values)) {
      setPreviewReady(false);
      return;
    }
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setPreviewReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [form, formSnapshot, previewLoading]);

  const showPreviewLoader = previewLoading || !previewReady;

  const headerTextColor = Form.useWatch(['defaultHeaderStyle', 'color'], form);
  const headerBgColor = Form.useWatch(['defaultHeaderStyle', 'backgroundColor'], form);
  const headerBorderColor = Form.useWatch(['defaultHeaderStyle', 'borderColor'], form);

  const lineTextColor = Form.useWatch(['defaultLineStyle', 'color'], form);
  const lineBgColor = Form.useWatch(['defaultLineStyle', 'backgroundColor'], form);
  const lineBorderColor = Form.useWatch(['defaultLineStyle', 'borderColor'], form);

  const nestedLineTextColor = Form.useWatch(['defaultNestedLineStyle', 'color'], form);
  const nestedLineBgColor = Form.useWatch(['defaultNestedLineStyle', 'backgroundColor'], form);
  const nestedLineBorderColor = Form.useWatch(['defaultNestedLineStyle', 'borderColor'], form);

  const deepNestedLineTextColor = Form.useWatch(['defaultDeepNestedLineStyle', 'color'], form);
  const deepNestedLineBgColor = Form.useWatch(['defaultDeepNestedLineStyle', 'backgroundColor'], form);
  const deepNestedLineBorderColor = Form.useWatch(['defaultDeepNestedLineStyle', 'borderColor'], form);

  const linkTextColor = Form.useWatch(['defaultLinkStyle', 'color'], form);
  const linkBgColor = Form.useWatch(['defaultLinkStyle', 'backgroundColor'], form);
  const linkBorderColor = Form.useWatch(['defaultLinkStyle', 'borderColor'], form);

  const pageTab = (
    <div className="style-modal-tab-panel">
      <StyleFieldGroup className="style-field-group--page-settings">
        <Tooltip title="Outer padding around the whole page in the reader">
          <Space size={6}>
            {SectionLabel.pagePadding}
            <Form.Item name="pagePaddingPx" style={{ marginBottom: 0 }}>
              <InputNumber
                size="small"
                min={0}
                max={64}
                step={1}
                style={{ width: 72 }}
                aria-label="Page padding"
              />
            </Form.Item>
          </Space>
        </Tooltip>
        <Tooltip title="Gap between each line of text in the reader">
          <Space size={6}>
            {SectionLabel.spacing}
            <Form.Item name="contentLineGapPx" style={{ marginBottom: 0 }}>
              <InputNumber size="small" min={0} max={48} step={1} style={{ width: 72 }} aria-label="Line gap" />
            </Form.Item>
          </Space>
        </Tooltip>
        <Tooltip title="Align all content left, center, right, or justified">
          <Space size={6}>
            {SectionLabel.layout}
            <Form.Item name="contentAlign" style={{ marginBottom: 0 }}>
              <Select
                size="small"
                style={{ width: 108 }}
                aria-label="Content align"
                onChange={(value) => applyContentAlignCascadeToForm(form, value)}
                options={[
                  { label: 'Left', value: 'left' },
                  { label: 'Center', value: 'center' },
                  { label: 'Right', value: 'right' },
                  { label: 'Justify', value: 'justify' },
                  { label: 'Justify align center', value: 'justify-center' },
                ]}
              />
            </Form.Item>
          </Space>
        </Tooltip>
      </StyleFieldGroup>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
        Page alignment applies to all style tabs. Change it here to reset header, line, and link alignment; each tab can still be customized separately until you change page alignment again.
      </Typography.Paragraph>
    </div>
  );

  return (
    <div className="book-default-styles-editor">
      <Tabs
        className="style-modal-tabs"
        size="small"
        type="card"
        activeKey={activeStyleTab}
        onChange={setActiveStyleTab}
        destroyOnHidden={false}
        items={[
          { key: 'page', label: SectionLabel.page, children: pageTab, forceRender: true },
          {
            key: 'headerAdvanced',
            label: SectionLabel.header,
            forceRender: true,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultHeaderStyle"
                mode="header"
                listValue={previewTextByTab.headerAdvanced}
                onListChange={(text) => setPreviewTextByTab((prev) => ({ ...prev, headerAdvanced: text }))}
                colors={{
                  color: headerTextColor,
                  backgroundColor: headerBgColor,
                  borderColor: headerBorderColor,
                }}
              />
            ),
          },
          {
            key: 'lineAdvanced',
            label: SectionLabel.line,
            forceRender: true,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultLineStyle"
                showSpan
                listValue={previewTextByTab.lineAdvanced}
                onListChange={(text) => setPreviewTextByTab((prev) => ({ ...prev, lineAdvanced: text }))}
                colors={{
                  color: lineTextColor,
                  backgroundColor: lineBgColor,
                  borderColor: lineBorderColor,
                }}
              />
            ),
          },
          {
            key: 'nestedLineAdvanced',
            label: SectionLabel.nestedLine,
            forceRender: true,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultNestedLineStyle"
                listValue={previewTextByTab.nestedLineAdvanced}
                onListChange={(text) => setPreviewTextByTab((prev) => ({ ...prev, nestedLineAdvanced: text }))}
                colors={{
                  color: nestedLineTextColor,
                  backgroundColor: nestedLineBgColor,
                  borderColor: nestedLineBorderColor,
                }}
              />
            ),
          },
          {
            key: 'deepNestedLineAdvanced',
            label: SectionLabel.deepNestedLine,
            forceRender: true,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultDeepNestedLineStyle"
                listValue={previewTextByTab.deepNestedLineAdvanced}
                onListChange={(text) => setPreviewTextByTab((prev) => ({ ...prev, deepNestedLineAdvanced: text }))}
                colors={{
                  color: deepNestedLineTextColor,
                  backgroundColor: deepNestedLineBgColor,
                  borderColor: deepNestedLineBorderColor,
                }}
              />
            ),
          },
          {
            key: 'linkAdvanced',
            label: SectionLabel.links,
            forceRender: true,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultLinkStyle"
                listValue={previewTextByTab.linkAdvanced}
                onListChange={(text) => setPreviewTextByTab((prev) => ({ ...prev, linkAdvanced: text }))}
                colors={{
                  color: linkTextColor,
                  backgroundColor: linkBgColor,
                  borderColor: linkBorderColor,
                }}
              />
            ),
          },
        ]}
      />

      <div className="book-default-styles-editor__preview">
        <Form.Item noStyle shouldUpdate>
          {() => {
            const values = form.getFieldsValue(true) as Record<string, unknown>;
            const contentLineGapPx = values.contentLineGapPx;
            const pagePaddingPx = values.pagePaddingPx;
            const contentAlign = values.contentAlign;
            const defaultTextColor = values.defaultTextColor;
            const defaultBackgroundColor = values.defaultBackgroundColor;
            const headerStyle = values.defaultHeaderStyle as Record<string, unknown> | undefined;
            const lineStyle = values.defaultLineStyle as Record<string, unknown> | undefined;
            const nestedLineStyle = values.defaultNestedLineStyle as Record<string, unknown> | undefined;
            const deepNestedLineStyle = values.defaultDeepNestedLineStyle as Record<string, unknown> | undefined;
            const linkStyle = values.defaultLinkStyle as Record<string, unknown> | undefined;

            const previewPagePadding = Math.max(0, Number(pagePaddingPx) || 0);
            const gap = Math.max(0, Number(contentLineGapPx) || 0);
            const align =
              contentAlign === 'center' ||
              contentAlign === 'right' ||
              contentAlign === 'justify' ||
              contentAlign === 'justify-center'
                ? contentAlign
                : 'left';
            const previewFlowStyle: React.CSSProperties = {
              display: 'flex',
              flexDirection: 'column',
              gap,
              lineHeight: 1,
              width: '100%',
            };
            if (align === 'center') {
              previewFlowStyle.textAlign = 'center';
              previewFlowStyle.alignItems = 'center';
            } else if (align === 'right') {
              previewFlowStyle.textAlign = 'right';
              previewFlowStyle.alignItems = 'flex-end';
            } else if (align === 'justify') {
              previewFlowStyle.textAlign = 'justify';
              previewFlowStyle.alignItems = 'stretch';
            } else if (align === 'justify-center') {
              previewFlowStyle.textAlign = 'justify';
              previewFlowStyle.textAlignLast = 'center';
              previewFlowStyle.alignItems = 'stretch';
            } else {
              previewFlowStyle.textAlign = 'left';
              previewFlowStyle.alignItems = 'stretch';
            }

            return (
              <Card
                size="small"
                styles={{
                  body: {
                    padding: '10px 12px',
                    ...(typeof defaultBackgroundColor === 'string' && defaultBackgroundColor.trim()
                      ? { backgroundColor: defaultBackgroundColor.trim() }
                      : {}),
                  },
                }}
              >
                {showPreviewLoader ? (
                  <Spin tip="Loading preview…">
                    <div style={{ minHeight: 160, width: '100%' }} aria-busy="true" aria-label="Loading preview" />
                  </Spin>
                ) : (
                  <div
                    style={{
                      padding: previewPagePadding,
                      background: previewPagePadding > 0 ? 'rgba(0, 0, 0, 0.03)' : undefined,
                      borderRadius: previewPagePadding > 0 ? 6 : undefined,
                      ...(activeStyleTab === 'page' && previewPagePadding > 0
                        ? { outline: '1px dashed #91caff', outlineOffset: -1 }
                        : {}),
                    }}
                  >
                    <div style={{ ...previewFlowStyle, minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
                      <div
                        style={{
                          ...HEADER_ALIGNMENT_HOST,
                          minWidth: 0,
                          ...previewRowStyle(activeStyleTab === 'headerAdvanced'),
                        }}
                      >
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                          Header / section
                        </Typography.Text>
                        <div style={buildAlignPositionHostStyle(
                          (() => {
                            const align = headerStyle?.align ?? headerStyle?.textAlign;
                            return align === 'left' ||
                              align === 'center' ||
                              align === 'right' ||
                              align === 'justify' ||
                              align === 'justify-center'
                              ? align
                              : undefined;
                          })(),
                        )}>
                          <span style={{ ...wrapHeaderStyleLayers(asPreviewCss(headerStyle)).chrome, whiteSpace: 'pre-wrap' }}>
                            {headerPreviewText}
                          </span>
                        </div>
                      </div>

                      <div style={{ width: '100%', minWidth: 0, ...previewRowStyle(activeStyleTab === 'lineAdvanced') }}>
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                          Line
                        </Typography.Text>
                        <div style={buildLinePreviewCss(lineStyle, defaultTextColor as string | undefined)}>
                          {linePreviewText}
                        </div>
                      </div>

                      <div style={{ width: '100%', minWidth: 0, ...previewRowStyle(activeStyleTab === 'nestedLineAdvanced') }}>
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                          Nested line (level 1)
                        </Typography.Text>
                        <div style={buildLinePreviewCss(nestedLineStyle, defaultTextColor as string | undefined)}>
                          {nestedLinePreviewText}
                        </div>
                      </div>

                      <div
                        style={{
                          width: '100%',
                          minWidth: 0,
                          ...previewRowStyle(activeStyleTab === 'deepNestedLineAdvanced'),
                          marginLeft: contentAlign === 'center' || contentAlign === 'right' ? 0 : 20,
                        }}
                      >
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                          Nested line (level 2+)
                        </Typography.Text>
                        <div style={buildLinePreviewCss(deepNestedLineStyle, defaultTextColor as string | undefined)}>
                          {deepNestedLinePreviewText}
                        </div>
                      </div>

                      <div style={{ width: '100%', ...previewRowStyle(activeStyleTab === 'linkAdvanced') }}>
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                          Link
                        </Typography.Text>
                        <span style={buildLinePreviewCss(lineStyle, defaultTextColor as string | undefined)}>
                          सम्बद्ध विधि{' '}
                          <span style={{ ...asPreviewCss(linkStyle), whiteSpace: 'pre-wrap' }}>{linkPreviewText}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          }}
        </Form.Item>
      </div>
    </div>
  );
};
